import React, { useState, useEffect, useRef } from "react";
import {
  FaSave,
  FaDownload,
  FaUpload,
  FaSearch,
  FaExpand,
  FaCog,
  FaProjectDiagram,
  FaExclamationTriangle,
  FaPlus,
  FaCode,
} from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import BpmnJS from "bpmn-js/dist/bpmn-modeler.production.min";
import "bpmn-js/dist/assets/diagram-js.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn-codes.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
import PropertiesPanel from "./designer_components/PropertiesPanel";
import LogicRouting from "./designer_components/LogicRouting";
import AxiosInstance from "axiosConfig";
import { jsonToBPMNXml } from "./designer_components/jsonToBPMN";
import Dialog from "./designer_components/Dialog";
import NewProcessModal from './designer_components/NewProcessModal';
import NodeMaintenanceModal from './designer_components/NodeMaintenanceModal';
import CodeEditor from "./CodeEditor";
import { CUSTOM_TYPES, MANDATORY_ATTRIBUTES } from './designer_components/constants';
import { validateTaskName, validateTaskNameUniqueness, validateFunctionDetails, validateSingleOutgoingRoute } from './designer_components/DesignValidator';

// Custom Hooks
const useBpmnModeler = (canvasRef, bpmnConfig) => {
  const [modeler, setModeler] = useState(null);

  useEffect(() => {
    if (!canvasRef.current || !bpmnConfig) return;

    const bpmnModeler = new BpmnJS({
      container: canvasRef.current,
      keyboard: { bindTo: document },
      moddleExtensions: {
        node: {
          prefix: "node",
          uri: bpmnConfig.nodeNamespace,
          xml: { tagAlias: "lowerCase" },
          types: [{ name: "NodeProperties", properties: CUSTOM_TYPES }],
        },
      },
    });

    setModeler(bpmnModeler);

    bpmnModeler.on("import.done", ({ error }) => {
      if (error) {
        console.error("Failed to import diagram", error);
        return;
      }
      bpmnModeler.get("canvas").zoom("fit-viewport");
    });

    return () => bpmnModeler.destroy();
  }, [canvasRef, bpmnConfig]);

  return modeler;
};

const useConversations = () => {
  const [conversations, setConversations] = useState([]);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await AxiosInstance.get("/get-conversations");
        setConversations(response.data);
      } catch (error) {
        console.error("Error fetching conversations", error);
      }
    };
    fetchConversations();
  }, []);

  return [conversations, setConversations];
};

const useBpmnConfig = () => {
  const [bpmnConfig, setBpmnConfig] = useState({
    nodeNamespace: "http://localhost:5000/node-fields",
    schemaLocation: "http://localhost:5000/node-fields",
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await AxiosInstance.get("/get-xsd-config");
        setBpmnConfig({
          nodeNamespace: response.data.node_namespace,
          schemaLocation: response.data.schema_location,
        });
      } catch (error) {
        console.error("Failed to fetch XSD config, using defaults", error);
      }
    };
    fetchConfig();
  }, []);

  return bpmnConfig;
};

const ErrorMarker = ({ element, modeler }) => {
  const [isMissingAttributes, setIsMissingAttributes] = useState(false);
  const [missingAttributes, setMissingAttributes] = useState([]);
  const [invalidName, setInvalidName] = useState(false);
  const [nonUniqueName, setNonUniqueName] = useState(false);
  const [invalidFunctionDetails, setInvalidFunctionDetails] = useState(false);
  const [hasMultipleOutgoingRoutes, setHasMultipleOutgoingRoutes] = useState(false);
  const [hasInsufficientOutgoingRoutes, setHasInsufficientOutgoingRoutes] = useState(false);
  const [missingRouteLogic, setMissingRouteLogic] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [ansDesc, setAnsDesc] = useState("");

  useEffect(() => {
    if (!element || !modeler) return;

    const checkMissingAttributes = () => {
      if (element.type === "bpmn:Task") {
        const missing = MANDATORY_ATTRIBUTES.filter(
          (attr) => !element.businessObject.get(attr === 'name' ? attr : `node:${attr}`)
        );
        const name = element.businessObject.get('name');
        const isValidName = name && /^[a-zA-Z0-9]+(_[a-zA-Z0-9]+)*$/.test(name);
        let isNonUniqueName = false;

        if (name && modeler) {
          const elementRegistry = modeler.get('elementRegistry');
          const tasks = elementRegistry.filter(
            (el) => el.type === 'bpmn:Task' && el.id !== element.id && el.businessObject.name
          );
          isNonUniqueName = tasks.some(
            (task) => task.businessObject.name && task.businessObject.name.toLowerCase() === name.toLowerCase()
          );
        }

        let isValidFunctionDetails = true;
        const isActionTrigger = element.businessObject.get('node:is_action_trigger') === 'true';
        if (isActionTrigger) {
          const funcDetails = element.businessObject.get('node:function_details');
          try {
            const parsed = funcDetails ? JSON.parse(funcDetails) : null;
            isValidFunctionDetails = parsed && parsed.id && parsed.func_name && typeof parsed.input_json === 'object';
          } catch (err) {
            isValidFunctionDetails = false;
          }
        }

        const hasMultipleRoutes = !validateSingleOutgoingRoute(element);
        const ansDescValue = element.businessObject.get('node:ans_desc') || "";

        setMissingAttributes(missing);
        setInvalidName(!!name && !isValidName);
        setNonUniqueName(isNonUniqueName);
        setInvalidFunctionDetails(isActionTrigger && !isValidFunctionDetails);
        setHasMultipleOutgoingRoutes(hasMultipleRoutes);
        setHasInsufficientOutgoingRoutes(false);
        setMissingRouteLogic(false);
        setIsMissingAttributes(
          missing.length > 0 || (name && !isValidName) || isNonUniqueName || (isActionTrigger && !isValidFunctionDetails) || hasMultipleRoutes
        );
        setAnsDesc(ansDescValue);
      } else if (element.type === "bpmn:ExclusiveGateway") {
        const outgoing = element.businessObject.get('outgoing') || [];
        const hasInsufficientRoutes = outgoing.length < 2;
        const routeLogic = element.businessObject.get('node:route_logic');
        const isMissingRouteLogic = !routeLogic || routeLogic.trim() === '';

        setMissingAttributes([]);
        setInvalidName(false);
        setNonUniqueName(false);
        setInvalidFunctionDetails(false);
        setHasMultipleOutgoingRoutes(false);
        setHasInsufficientOutgoingRoutes(hasInsufficientRoutes);
        setMissingRouteLogic(isMissingRouteLogic);
        setIsMissingAttributes(hasInsufficientRoutes || isMissingRouteLogic);
        setAnsDesc("");
      } else {
        const hasMultipleRoutes = !validateSingleOutgoingRoute(element);
        setMissingAttributes([]);
        setInvalidName(false);
        setNonUniqueName(false);
        setInvalidFunctionDetails(false);
        setHasMultipleOutgoingRoutes(hasMultipleRoutes);
        setHasInsufficientOutgoingRoutes(false);
        setMissingRouteLogic(false);
        setIsMissingAttributes(hasMultipleRoutes);
        setAnsDesc("");
      }
    };

    checkMissingAttributes();

    const eventBus = modeler.get("eventBus");
    const onChange = () => checkMissingAttributes();

    eventBus.on("element.changed", onChange);
    return () => eventBus.off("element.changed", onChange);
  }, [element, modeler]);

  const canvas = modeler?.get("canvas");
  if (!canvas) return null;

  const position = canvas.getAbsoluteBBox(element);

  return (
    <div
      className="absolute"
      style={{
        left: `${position.x + position.width / 2 - 10}px`,
        top: `${position.y + position.height / 2 - 10}px`,
        width: isMissingAttributes ? '20px' : '40px',
        height: isMissingAttributes ? '20px' : '40px',
        backgroundColor: isMissingAttributes ? 'transparent' : 'transparent',
        cursor: isMissingAttributes ? 'pointer' : 'default',
      }}
      onMouseEnter={() => {
        console.log('Hovering:', { elementId: element.id, ansDesc, isMissingAttributes });
        setShowTooltip(true);
      }}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {isMissingAttributes ? (
        <>
          <FaExclamationTriangle className="text-red-500 text-xl cursor-pointer" />
          {showTooltip && (
            <div
              className="absolute bg-white p-2 rounded shadow-lg border border-gray-200 text-xs whitespace-nowrap z-50"
              style={{
                top: "25px",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            >
              {missingAttributes.length > 0 && (
                <>
                  Missing required fields:
                  <ul className="list-disc pl-4 mt-1">
                    {missingAttributes.map((attr) => (
                      <li key={attr}>{attr}</li>
                    ))}
                  </ul>
                </>
              )}
              {invalidName && (
                <div className={missingAttributes.length > 0 ? "mt-2" : ""}>
                  Invalid task name: Must be a single word or words joined with underscores (e.g., Task_Name)
                </div>
              )}
              {nonUniqueName && (
                <div className={(missingAttributes.length > 0 || invalidName) ? "mt-2" : ""}>
                  Task name must be unique (case-insensitive)
                </div>
              )}
              {invalidFunctionDetails && (
                <div className={(missingAttributes.length > 0 || invalidName || nonUniqueName) ? "mt-2" : ""}>
                  Invalid function details: Must include id, func_name, and input_json
                </div>
              )}
              {hasMultipleOutgoingRoutes && (
                <div className={(missingAttributes.length > 0 || invalidName || nonUniqueName || invalidFunctionDetails) ? "mt-2" : ""}>
                  Task must have exactly one outgoing route
                </div>
              )}
              {hasInsufficientOutgoingRoutes && (
                <div className={(missingAttributes.length > 0 || invalidName || nonUniqueName || invalidFunctionDetails || hasMultipleOutgoingRoutes) ? "mt-2" : ""}>
                  Exclusive Gateway must have at least two outgoing routes
                </div>
              )}
              {missingRouteLogic && (
                <div className={(missingAttributes.length > 0 || invalidName || nonUniqueName || invalidFunctionDetails || hasMultipleOutgoingRoutes || hasInsufficientOutgoingRoutes) ? "mt-2" : ""}>
                  Exclusive Gateway must have route_logic defined
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        ansDesc && (
          <div
            className="absolute w-full h-full"
            style={{ top: 0, left: 0 }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {showTooltip && (
              <div
                className="absolute bg-white p-2 rounded shadow-lg border border-gray-200 text-xs whitespace-nowrap z-50"
                style={{
                  top: "25px",
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
              >
                {ansDesc}
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};

const DesignerViewBPMNS = () => {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [diagramName, setDiagramName] = useState("New Process");
  const [selectedElement, setSelectedElement] = useState(null);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);
  const [showLogicRouting, setShowLogicRouting] = useState(false);
  const [showNewProcessModal, setShowNewProcessModal] = useState(false);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [showCodeEditorModal, setShowCodeEditorModal] = useState(false);
  const [gearPosition, setGearPosition] = useState({ x: 0, y: 0 });
  const [elements, setElements] = useState([]);
  const [processFormData, setProcessFormData] = useState({
    form_phrase: "",
    convo_name: "",
    convo_description: "",
    form_type: "",
    max_allowed_attempts: 1,
    informs_sender_if_maxed: true,
    allows_retry_after_days: 9999,
    stems_logic_in_num: false,
    skip_consent_prompt: false,
  });
  const [nodeFormData, setNodeFormData] = useState({
    node_key_words: "",
    ans_desc: "",
    que_topic: "",
    bot_act_as: "",
    que_category: "",
    culture: "",
    enforces_match: false,
    comments: "",
    node_number: "",
    nodeDetails: "",
  });
  const [errors, setErrors] = useState({});
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const bpmnConfig = useBpmnConfig();
  const [conversations, setConversations] = useConversations();
  const modeler = useBpmnModeler(canvasRef, bpmnConfig);
  const [BPMN_NAMESPACES, setBpmnNamespaces] = useState({
    "xmlns:bpmn": "http://www.omg.org/spec/BPMN/20100524/MODEL",
    "xmlns:bpmndi": "http://www.omg.org/spec/BPMN/20100524/DI",
    "xmlns:dc": "http://www.omg.org/spec/DD/20100524/DC",
    "xmlns:di": "http://www.omg.org/spec/DD/20100524/DI",
    "xmlns:node": "http://localhost:5000/node-fields",
    "xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
    "xsi:schemaLocation": "http://localhost:5000/node-fields http://localhost:5000/node-fields",
  });

  useEffect(() => {
    if (!bpmnConfig) return;

    setBpmnNamespaces((prev) => ({
      ...prev,
      "xmlns:node": bpmnConfig.nodeNamespace,
      "xsi:schemaLocation": `${bpmnConfig.nodeNamespace} ${bpmnConfig.schemaLocation}`,
    }));
  }, [bpmnConfig]);

  useEffect(() => {
    if (!modeler) return;

    const handleElementClick = ({ element }) => {
      setSelectedElement(element);
      const canvas = modeler.get("canvas");
      const position = canvas.getAbsoluteBBox(element);
      setGearPosition({
        x: position.x + position.width / 2 - 12,
        y: position.y - 30,
      });
      setShowPropertiesPanel(false);
      setShowLogicRouting(false);
    };

    const handleCanvasClick = () => {
      setSelectedElement(null);
      setGearPosition({ x: 0, y: 0 });
      setShowPropertiesPanel(false);
      setShowLogicRouting(false);
    };

    const updateElements = () => {
      const elementRegistry = modeler.get("elementRegistry");
      setElements(elementRegistry.getAll());
    };

    modeler.on("element.click", handleElementClick);
    modeler.on("canvas.click", handleCanvasClick);
    modeler.on("elements.changed", updateElements);
    modeler.on("import.done", updateElements);

    return () => {
      modeler.off("element.click", handleElementClick);
      modeler.off("canvas.click", handleCanvasClick);
      modeler.off("elements.changed", updateElements);
      modeler.off("import.done", updateElements);
    };
  }, [modeler]);

  const validateDiagramEvents = (elementRegistry) => {
    const startEvents = elementRegistry.filter(
      (element) => element.type === "bpmn:StartEvent"
    );
    const endEvents = elementRegistry.filter(
      (element) => element.type === "bpmn:EndEvent"
    );
    return {
      hasStartEvent: startEvents.length === 1,
      hasEndEvent: endEvents.length >= 1,
    };
  };

  const validateExclusiveGateways = (elementRegistry) => {
    const gateways = elementRegistry.filter(
      (element) => element.type === "bpmn:ExclusiveGateway"
    );
    const invalidGateways = gateways.filter((gateway) => {
      const outgoing = gateway.businessObject.get('outgoing') || [];
      const routeLogic = gateway.businessObject.get('node:route_logic');
      const hasInsufficientOutgoingRoutes = outgoing.length < 2;
      const isMissingRouteLogic = !routeLogic || routeLogic.trim() === '';
      return hasInsufficientOutgoingRoutes || isMissingRouteLogic;
    });
    return {
      invalidGateways,
      hasValidGateways: invalidGateways.length === 0,
    };
  };

  const handleExport = async () => {
    try {
      const elementRegistry = modeler.get("elementRegistry");
      const allTasks = elementRegistry.filter(
        (element) => element.type === "bpmn:Task"
      );

      const invalidTasks = allTasks.filter((task) => {
        const missingAttrs = MANDATORY_ATTRIBUTES.filter(
          (attr) => !task.businessObject.get(attr === 'name' ? attr : `node:${attr}`)
        );
        const invalidName = !validateTaskName(task);
        const invalidFuncDetails = !validateFunctionDetails(task);
        const invalidRoutes = !validateSingleOutgoingRoute(task);
        return missingAttrs.length > 0 || invalidName || invalidFuncDetails || invalidRoutes;
      });

      const duplicateNames = validateTaskNameUniqueness(allTasks);
      const { hasStartEvent, hasEndEvent } = validateDiagramEvents(elementRegistry);
      const { invalidGateways, hasValidGateways } = validateExclusiveGateways(elementRegistry);

      if (invalidTasks.length > 0 || duplicateNames.length > 0 || !hasStartEvent || !hasEndEvent || !hasValidGateways) {
        const errorMessages = [];
        if (invalidTasks.length > 0) {
          errorMessages.push(
            "Some tasks have issues:",
            "- Missing required attributes (name, ans_desc, node_key_words).",
            "- Invalid task names (must be a single word or words joined with underscores, e.g., Task_Name, and cannot be empty).",
            "- Invalid function details (must include id, func_name, and input_json when is_action_trigger is true).",
            "- Tasks with multiple outgoing routes (must have exactly one)."
          );
        }
        if (duplicateNames.length > 0) {
          errorMessages.push(
            "Non-unique task names (case-insensitive):",
            ...duplicateNames.map(({ id, name }) => `- Task ID: ${id}, Name: ${name}`)
          );
        }
        if (!hasStartEvent) {
          errorMessages.push("Diagram must have exactly one Start Event.");
        }
        if (!hasEndEvent) {
          errorMessages.push("Diagram must have at least one End Event.");
        }
        if (!hasValidGateways) {
          errorMessages.push(
            "Exclusive gateways with issues:",
            ...invalidGateways.flatMap((gateway) => {
              const outgoing = gateway.businessObject.get('outgoing') || [];
              const routeLogic = gateway.businessObject.get('node:route_logic');
              const issues = [];
              if (outgoing.length < 2) {
                issues.push(`- Gateway ID: ${gateway.id} has fewer than two outgoing routes.`);
              }
              if (!routeLogic || routeLogic.trim() === '') {
                issues.push(`- Gateway ID: ${gateway.id} is missing route_logic.`);
              }
              return issues;
            })
          );
        }
        errorMessages.push("Please fix these issues before exporting.");

        setDialogConfig({
          isOpen: true,
          title: "Validation Error",
          message: errorMessages.join("\n"),
          onConfirm: null,
        });
        return;
      }

      const { xml } = await modeler.saveXML({ format: true });
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, "application/xml");
      const definitions = xmlDoc.getElementsByTagName("bpmn:definitions")[0];

      Object.entries(BPMN_NAMESPACES).forEach(([key, value]) => {
        if (!definitions.getAttribute(key)) {
          definitions.setAttribute(key, value);
        }
      });

      const serializer = new XMLSerializer();
      const finalXml = serializer.serializeToString(xmlDoc);
      const blob = new Blob([finalXml], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${diagramName}.bpmn`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDialogConfig({
        isOpen: true,
        title: "Success",
        message: "Diagram exported successfully!",
        onConfirm: null,
      });
    } catch (err) {
      setDialogConfig({
        isOpen: true,
        title: "Error",
        message: `Failed to export diagram: ${err.message || err}`,
        onConfirm: null,
      });
    }
  };

  const handleSave = async () => {
    try {
      const elementRegistry = modeler.get("elementRegistry");
      const allTasks = elementRegistry.filter(
        (element) => element.type === "bpmn:Task"
      );

      const invalidTasks = allTasks.filter((task) => {
        const missingAttrs = MANDATORY_ATTRIBUTES.filter(
          (attr) => !task.businessObject.get(attr === 'name' ? attr : `node:${attr}`)
        );
        const invalidName = !validateTaskName(task);
        const invalidFuncDetails = !validateFunctionDetails(task);
        const invalidRoutes = !validateSingleOutgoingRoute(task);
        return missingAttrs.length > 0 || invalidName || invalidFuncDetails || invalidRoutes;
      });

      const duplicateNames = validateTaskNameUniqueness(allTasks);
      const { hasStartEvent, hasEndEvent } = validateDiagramEvents(elementRegistry);
      const { invalidGateways, hasValidGateways } = validateExclusiveGateways(elementRegistry);

      if (invalidTasks.length > 0 || duplicateNames.length > 0 || !hasStartEvent || !hasEndEvent || !hasValidGateways) {
        const errorMessages = [];
        if (invalidTasks.length > 0) {
          errorMessages.push(
            "Some tasks have issues:",
            "- Missing required attributes (name, ans_desc, node_key_words).",
            "- Invalid task names (must be a single word or words joined with underscores, e.g., Task_Name, and cannot be empty).",
            "- Invalid function details (must include id, func_name, and input_json when is_action_trigger is true).",
            "- Tasks with multiple outgoing routes (must have exactly one)."
          );
        }
        if (duplicateNames.length > 0) {
          errorMessages.push(
            "Non-unique task names (case-insensitive):",
            ...duplicateNames.map(({ id, name }) => `- Task ID: ${id}, Name: ${name}`)
          );
        }
        if (!hasStartEvent) {
          errorMessages.push("Diagram must have exactly one Start Event.");
        }
        if (!hasEndEvent) {
          errorMessages.push("Diagram must have at least one End Event.");
        }
        if (!hasValidGateways) {
          errorMessages.push(
            "Exclusive gateways with issues:",
            ...invalidGateways.flatMap((gateway) => {
              const outgoing = gateway.businessObject.get('outgoing') || [];
              const routeLogic = gateway.businessObject.get('node:route_logic');
              const issues = [];
              if (outgoing.length < 2) {
                issues.push(`- Gateway ID: ${gateway.id} has fewer than two outgoing routes.`);
              }
              if (!routeLogic || routeLogic.trim() === '') {
                issues.push(`- Gateway ID: ${gateway.id} is missing route_logic.`);
              }
              return issues;
            })
          );
        }
        errorMessages.push("Please fix these issues before saving.");

        setDialogConfig({
          isOpen: true,
          title: "Validation Error",
          message: errorMessages.join("\n"),
          onConfirm: null,
        });
        return;
      }

      const { xml } = await modeler.saveXML({ format: true });
      const response = await AxiosInstance.post("/save-model", { xml });
      setDialogConfig({
        isOpen: true,
        title: "Success",
        message: response.data?.message || "Model saved successfully!",
        onConfirm: null,
      });
    } catch (err) {
      setDialogConfig({
        isOpen: true,
        title: "Error",
        message: `Failed to save model: ${err.message || err}`,
        onConfirm: null,
      });
    }
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        await modeler.importXML(event.target.result);
        setDiagramName(file.name.replace(".bpmn", ""));
        setDialogConfig({
          isOpen: true,
          title: "Success",
          message: "Diagram imported successfully!",
          onConfirm: null,
        });
      } catch (err) {
        setDialogConfig({
          isOpen: true,
          title: "Error",
          message: `Failed to import diagram: ${err.message || err}`,
          onConfirm: null,
        });
      }
    };
    reader.readAsText(file);
  };

  const handleZoom = (type) => {
    const canvas = modeler?.get("canvas");
    if (!canvas) return;

    const currentZoom = canvas.zoom();
    if (type === "in") {
      canvas.zoom(Math.min(currentZoom + 0.2, 3));
    } else if (type === "out") {
      canvas.zoom(Math.max(currentZoom - 0.2, 0.2));
    } else if (type === "fit") {
      canvas.zoom("fit-viewport");
    }
  };

  const handleNewProcessSubmit = async (data) => {
    try {
      const processData = {
        form_phrase: data.form_phrase,
        convo_name: data.convo_name,
        convo_description: data.convo_description,
        form_type: data.form_type,
        max_allowed_attempts: data.max_allowed_attempts,
        informs_sender_if_maxed: data.informs_sender_if_maxed,
        allows_retry_after_days: data.allows_retry_after_days,
        stems_logic_in_num: data.stems_logic_in_num,
        skip_consent_prompt: data.skip_consent_prompt,
      };
      const response = await AxiosInstance.post("/conversations-create", processData);
      const selectedId = response.data?.convo_id || response.data?.convo_name;
      const theNewXML = response.data?.start_model;

      if (!selectedId || !theNewXML) {
        throw new Error("Invalid response: Missing convo_id or start_model");
      }

      const conversationsResponse = await AxiosInstance.get("/get-conversations");
      setConversations(conversationsResponse.data);
      localStorage.setItem("selectedProcessId", selectedId);
      await modeler.importXML(theNewXML);
      setDiagramName(selectedId);

      setDialogConfig({
        isOpen: true,
        title: "Success",
        message: response.data?.message || "Process created successfully!",
        onConfirm: () => {
          setShowNewProcessModal(false);
          setProcessFormData({
            form_phrase: "",
            convo_name: "",
            convo_description: "",
            form_type: "",
            max_allowed_attempts: 1,
            informs_sender_if_maxed: true,
            allows_retry_after_days: 9999,
            stems_logic_in_num: false,
            skip_consent_prompt: false
          });
          setErrors({});
        },
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "An unexpected error occurred";
      setDialogConfig({
        isOpen: true,
        title: "Error",
        message: `Failed to create process: ${errorMessage}`,
        onConfirm: null,
      });
    }
  };

  const handleNodeSubmit = async (data) => {
    try {
      const nodeData = {
        node_key_words: data.node_key_words,
        ans_desc: data.ans_desc,
        que_topic: data.que_topic,
        bot_act_as: data.bot_act_as,
        que_category: data.que_category,
        culture: data.culture,
        enforces_match: data.enforces_match,
        comments: data.comments,
      };

      const response = await AxiosInstance.post("/node-creation", nodeData);

      setDialogConfig({
        isOpen: true,
        title: "Success",
        message: response.data?.message || "Node created successfully!",
        onConfirm: () => {
          setShowNodeModal(false);
          setNodeFormData({
            node_key_words: "",
            ans_desc: "",
            que_topic: "",
            bot_act_as: "",
            que_category: "",
            culture: "",
            enforces_match: false,
            comments: "",
            node_number: "",
            nodeDetails: "",
          });
          setErrors({});
        },
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "An unexpected error occurred";
      setDialogConfig({
        isOpen: true,
        title: "Error",
        message: `Failed to create node: ${errorMessage}`,
        onConfirm: null,
      });
    }
  };

  const handleRetrieveNode = async (nodeNumber) => {
    try {
      setNodeFormData((prev) => ({
        ...prev,
        nodeDetails: `Details for Node ${nodeNumber} will be displayed here.`,
      }));
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "An unexpected error occurred";
      setDialogConfig({
        isOpen: true,
        title: "Error",
        message: `Failed to retrieve node: ${errorMessage}`,
        onConfirm: null,
      });
    }
  };

  const handleSelectProcess = async (e) => {
    const selectedId = e.target.value;
    setDiagramName(selectedId);
    if (selectedId) {
      localStorage.setItem("selectedProcessId", selectedId);
      try {
        const response = await AxiosInstance.post("/get-convo-model-in-json", {
          conversation_id: parseInt(selectedId),
        });
        const the_json = response.data?.message;
        if (!the_json) {
          throw new Error("Missing 'message' in response.");
        }
        const bpmnXml = jsonToBPMNXml(the_json);
        console.log(bpmnXml)
        await modeler.importXML(bpmnXml);
      } catch (err) {
        setDialogConfig({
          isOpen: true,
          title: "Error",
          message: `Failed to get conversation model: ${err.message || err}`,
          onConfirm: null,
        });
      }
    }
  };

  const handleGearClick = () => {
    if (selectedElement?.type === "bpmn:ExclusiveGateway") {
      setShowLogicRouting(!showLogicRouting);
      setShowPropertiesPanel(false);
    } else {
      setShowPropertiesPanel(!showPropertiesPanel);
      setShowLogicRouting(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <header className="bg-white border-b p-2 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h3 className="text-xl font-semibold">BPMN Designer</h3>
          <select
            value={diagramName}
            onChange={handleSelectProcess}
            className="border p-1 rounded w-64"
          >
            <option value="">Select Process</option>
            {Object.entries(conversations)
              .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
              .map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
          </select>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowCodeEditorModal(true)}
            className="flex items-center space-x-1 bg-gray-500 text-white px-3 py-1 rounded"
            aria-label="Open Code Editor"
          >
            <FaCode />
            <span>Scripts</span>
          </button>
          <button
            onClick={() => setShowNodeModal(true)}
            className="flex items-center space-x-1 bg-gray-500 text-white px-3 py-1 rounded"
          >
            <FaPlus />
            <span>Add Node</span>
          </button>
          <button
            onClick={() => setShowNewProcessModal(true)}
            className="flex items-center space-x-1 bg-gray-500 text-white px-3 py-1 rounded"
          >
            <FaProjectDiagram />
            <span>New</span>
          </button>
          <button
            onClick={handleSave}
            className="flex items-center space-x-1 bg-blue-500 text-white px-3 py-1 rounded"
          >
            <FaSave />
            <span>Save</span>
          </button>
          <button
            onClick={handleExport}
            className="flex items-center space-x-1 bg-blue-500 text-white px-3 py-1 rounded"
          >
            <FaDownload />
            <span>Export</span>
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImport}
            accept=".bpmn,.xml"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current.click()}
            className="flex items-center space-x-1 bg-gray-500 text-white px-3 py-1 rounded"
          >
            <FaUpload />
            <span>Import</span>
          </button>
        </div>
      </header>
      <div className="bg-white border-b p-1 flex justify-center space-x-2">
        <button
          onClick={() => handleZoom("in")}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <FaSearch className="transform rotate-90" />
        </button>
        <button
          onClick={() => handleZoom("out")}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <FaSearch className="transform -rotate-90" />
        </button>
        <button
          onClick={() => handleZoom("fit")}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <FaExpand />
        </button>
      </div>
      <main className="flex flex-1 overflow-hidden">
        <div
          ref={canvasRef}
          className="flex-1 bg-white overflow-hidden relative"
          style={{
            backgroundImage:
              'url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2U1ZTVlNSIgb3BhY2l0eT0iMC4yIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=")',
          }}
        >
          {elements.map((element) => (
            <ErrorMarker key={element.id} element={element} modeler={modeler} />
          ))}
          {selectedElement && (
            <button
              className="absolute bg-white p-1 rounded-full shadow-md hover:bg-gray-100 transition-colors"
              style={{ left: `${gearPosition.x}px`, top: `${gearPosition.y}px` }}
              onClick={handleGearClick}
            >
              <FaCog className="text-blue-500 text-lg" />
            </button>
          )}
          {showLogicRouting && selectedElement?.type === "bpmn:ExclusiveGateway" && (
            <LogicRouting modeler={modeler} onClose={() => setShowLogicRouting(false)} isActive={showLogicRouting} />
          )}
        </div>
        {showPropertiesPanel && (
          <div className="w-80 bg-white border-l overflow-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="font-medium">Properties</h3>
              <button
                onClick={() => setShowPropertiesPanel(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <IoClose size={20} />
              </button>
            </div>
            {selectedElement ? (
              <PropertiesPanel element={selectedElement} modeler={modeler} />
            ) : (
              <div className="p-4 text-gray-500">No element selected</div>
            )}
          </div>
        )}
      </main>
      <NewProcessModal
        isOpen={showNewProcessModal}
        onClose={() => setShowNewProcessModal(false)}
        onSubmit={handleNewProcessSubmit}
        formData={processFormData}
        setFormData={setProcessFormData}
        errors={errors}
        setErrors={setErrors}
      />
      <NodeMaintenanceModal
        isOpen={showNodeModal}
        onClose={() => setShowNodeModal(false)}
        onSubmit={handleNodeSubmit}
        formData={nodeFormData}
        setFormData={setNodeFormData}
        errors={errors}
        setErrors={setErrors}
        onRetrieveNode={handleRetrieveNode}
      />
      {showCodeEditorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-11/12 max-w-4xl h-5/6 flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-xl font-semibold text-black">Code Editor</h3>
              <button
                onClick={() => setShowCodeEditorModal(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close Code Editor"
              >
                <IoClose size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              <CodeEditor />
            </div>
          </div>
        </div>
      )}
      <Dialog
        isOpen={dialogConfig.isOpen}
        onClose={() =>
          setDialogConfig((prev) => ({ ...prev, isOpen: false }))
        }
        title={dialogConfig.title}
        message={dialogConfig.message}
        onConfirm={dialogConfig.onConfirm}
      />
    </div>
  );
};

export default DesignerViewBPMNS;